import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';

/**
 * UserAutocomplete - Autocomplete input for selecting registered users
 *
 * Props:
 * - value: string (current input value / player name)
 * - selectedUserId: number | null (ID of selected user)
 * - onChange: (name: string, userId: number | null) => void
 * - placeholder: string
 * - required: boolean
 */
function UserAutocomplete({
  value,
  selectedUserId,
  onChange,
  placeholder = "Spielername eingeben...",
  required = false
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const API_URL = process.env.REACT_APP_API_URL || '';

  // Update dropdown position when opening
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width + 20
      });
    }
  };

  // Close dropdown on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  // Sync input value with prop
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions with debounce
  const fetchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/users/search`, {
        params: { q: query }
      });
      setSuggestions(response.data);
      updateDropdownPosition();
      setIsOpen(true);
    } catch (err) {
      console.error('Error fetching user suggestions:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setHighlightedIndex(-1);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce API call
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);

    // Notify parent of text change (clear user selection)
    onChange(newValue, null);
  };

  const handleSelectUser = (user) => {
    const displayName = user.display_name;
    setInputValue(displayName);
    setIsOpen(false);
    setSuggestions([]);
    onChange(displayName, user.id);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectUser(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setIsOpen(false);
    onChange('', null);
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.length >= 2 && suggestions.length > 0) {
              updateDropdownPosition();
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          required={required}
          style={{
            width: 'calc(100% - 20px)',
            paddingRight: selectedUserId ? '30px' : '10px'
          }}
        />
        {selectedUserId && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 6px',
              fontSize: '14px',
              color: 'var(--text-secondary, #6c757d)'
            }}
            title="Auswahl aufheben"
          >
            x
          </button>
        )}
        {loading && (
          <span style={{
            position: 'absolute',
            right: selectedUserId ? '30px' : '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '12px',
            color: 'var(--text-secondary, #6c757d)'
          }}>
            ...
          </span>
        )}
      </div>

      {/* Dropdown - rendered via Portal to avoid overflow clipping */}
      {isOpen && suggestions.length > 0 && ReactDOM.createPortal(
        <ul style={{
          position: 'fixed',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          backgroundColor: 'var(--card-bg, white)',
          border: '1px solid var(--border-color, #dee2e6)',
          borderRadius: '4px',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 9999,
          margin: 0,
          padding: 0,
          listStyle: 'none',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)'
        }}>
          {suggestions.map((user, index) => (
            <li
              key={user.id}
              onClick={() => handleSelectUser(user)}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                backgroundColor: index === highlightedIndex
                  ? 'rgba(232, 183, 58, 0.3)'
                  : 'var(--card-bg, white)',
                borderBottom: index < suggestions.length - 1
                  ? '1px solid var(--border-color, #eee)'
                  : 'none',
                color: 'var(--text-color, #333)'
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div style={{ fontWeight: 500 }}>{user.display_name}</div>
              {user.first_name && user.last_name && user.display_name !== `${user.first_name} ${user.last_name}` && (
                <div style={{ fontSize: '0.8em', color: 'var(--text-secondary, #6c757d)' }}>
                  {user.first_name} {user.last_name}
                </div>
              )}
            </li>
          ))}
        </ul>,
        document.body
      )}

      {/* Visual indicator for linked user */}
      {selectedUserId && (
        <small style={{
          display: 'block',
          marginTop: '2px',
          color: '#22c55e',
          fontSize: '0.75rem'
        }}>
          Registrierter Benutzer verknuepft
        </small>
      )}
    </div>
  );
}

export default UserAutocomplete;
